import { EsPCAReportField, PCAReportField } from 'c-sdk';
import type { TranslationPath } from 'c-translation';
import { MapDataType } from 'c-main/Components/Campaign/CampaignReports/types';

export type SummaryPresetKey = 'default' | 'test';
export type SummaryWidgetMetric = string;
export type SummaryWidgetSource = 'summary' | 'groupByField' | 'frameIdMapData' | 'custom';
export type SummaryWidgetKind = 'line' | 'bar' | 'map';
export type SummaryWidgetGroupBy = string;
export type SummaryMapDisplayMode = 'heatmap' | 'screens' | 'both';

export type SummaryWidgetConfig = {
    id: string;
    metric: SummaryWidgetMetric;
    source: SummaryWidgetSource;
    kind: SummaryWidgetKind;
    groupBy: SummaryWidgetGroupBy[];
    mapDataType?: MapDataType;
    mapDisplayMode?: SummaryMapDisplayMode;
    title?: string;
};

export const SUMMARY_WIDGET_GROUP_BY_TRANSLATION_KEYS: Partial<
    Record<SummaryWidgetGroupBy, TranslationPath>
> = {
    date: 'Modules.Main.Campaigns.Overview.Reports.fields.day',
    display_owner: 'Modules.Main.Campaigns.Overview.Reports.fields.display_owner',
    display_env: 'Modules.Main.Campaigns.Overview.Reports.fields.display_env',
    display_format: 'Modules.Main.Campaigns.Overview.Reports.fields.display_format',
    display_owner_format: 'Modules.Main.Campaigns.Overview.Reports.fields.display_owner_format',
    display_tv_region: 'Modules.Main.Campaigns.Overview.Reports.fields.display_tv_region',
    day_of_week: 'Modules.Main.Campaigns.Overview.Reports.fields.dow',
    hour: 'Modules.Main.Campaigns.Overview.Reports.fields.hour',
    map: 'Modules.Main.Campaigns.Overview.Analytics.summaryTitleHeatmap',
};

export const SUMMARY_WIDGET_GROUP_BY_ES_FIELD_MAP: Partial<
    Record<SummaryWidgetGroupBy, EsPCAReportField>
> = {
    display_owner: EsPCAReportField.DisplayOwner,
    display_env: EsPCAReportField.DisplayEnv,
    display_format: EsPCAReportField.DisplayFormat,
    display_tv_region: EsPCAReportField.DisplayTvRegion,
    day_of_week: EsPCAReportField.Dow,
    hour: EsPCAReportField.Hour,
};

export const SUMMARY_WIDGET_GROUP_BY_PCA_FIELD_MAP: Partial<
    Record<SummaryWidgetGroupBy, PCAReportField>
> = {
    display_owner: PCAReportField.MediaOwner,
    display_tv_region: PCAReportField.TvRegion,
    day_of_week: PCAReportField.DayOfWeek,
    hour: PCAReportField.Hour,
};

export const SUMMARY_WIDGET_METRIC_OPTIONS = [
    { value: 'impacts', labelPath: 'Modules.Main.Campaigns.Overview.Analytics.impactsTitle' },
    { value: 'plays', labelPath: 'Modules.Main.Campaigns.Overview.Analytics.playsTitle' },
] as const;

export const SUMMARY_WIDGET_KIND_OPTIONS = [
    {
        value: 'line',
        labelPath: 'Modules.Main.Campaigns.Overview.SummaryCustomization.chartTypes.line',
    },
    {
        value: 'bar',
        labelPath: 'Modules.Main.Campaigns.Overview.SummaryCustomization.chartTypes.bar',
    },
    {
        value: 'map',
        labelPath: 'Modules.Main.Campaigns.Overview.SummaryCustomization.chartTypes.map',
    },
] as const;

export const SUMMARY_WIDGET_MAP_DATA_TYPE_OPTIONS = [
    {
        value: MapDataType.HeatActual,
        labelPath: 'actual',
    },
    {
        value: MapDataType.HeatExpected,
        labelPath: 'expected',
    },
    {
        value: MapDataType.HeatPerformance,
        labelPath: 'performance',
    },
] as const;

export const SUMMARY_WIDGET_MAP_DISPLAY_OPTIONS = [
    {
        value: 'heatmap',
        labelPath: 'Modules.Main.Campaigns.Overview.SummaryCustomization.heatmapOnly',
    },
    { value: 'screens', labelPath: 'Modules.Main.Campaigns.Overview.Reports.map.markersToggle' },
    {
        value: 'both',
        labelPath: 'Modules.Main.Campaigns.Overview.SummaryCustomization.heatmapAndScreens',
    },
] as const;

export const SUMMARY_WIDGET_GROUP_BY_OPTIONS = [
    'date',
    'display_owner',
    'display_env',
    'display_format',
    'display_tv_region',
    'day_of_week',
    'hour',
] as const;

export const SUMMARY_WIDGET_PRESETS: Record<
    SummaryPresetKey,
    { standard: SummaryWidgetConfig[]; dsp: SummaryWidgetConfig[] }
> = {
    default: {
        standard: [
            {
                id: 'running_total_impacts',
                metric: 'impacts',
                source: 'summary',
                kind: 'line',
                groupBy: ['date'],
            },
            {
                id: 'running_total_plays',
                metric: 'plays',
                source: 'summary',
                kind: 'line',
                groupBy: ['date'],
            },
            {
                id: 'impacts_by_media_owner',
                metric: 'impacts',
                source: 'summary',
                kind: 'bar',
                groupBy: ['display_owner'],
            },
            {
                id: 'impacts_by_region',
                metric: 'impacts',
                source: 'summary',
                kind: 'bar',
                groupBy: ['display_tv_region'],
            },
            {
                id: 'impacts_by_day_of_week',
                metric: 'impacts',
                source: 'summary',
                kind: 'bar',
                groupBy: ['day_of_week'],
            },
            {
                id: 'impacts_by_hour',
                metric: 'impacts',
                source: 'groupByField',
                kind: 'bar',
                groupBy: ['hour'],
            },
            {
                id: 'impacts_heatmap',
                metric: 'impacts',
                source: 'summary',
                kind: 'map',
                groupBy: [],
                mapDataType: MapDataType.HeatActual,
                mapDisplayMode: 'heatmap',
            },
        ],
        dsp: [
            {
                id: 'running_total_impacts',
                metric: 'impacts',
                source: 'summary',
                kind: 'line',
                groupBy: ['date'],
            },
            {
                id: 'impacts_by_media_owner',
                metric: 'impacts',
                source: 'summary',
                kind: 'bar',
                groupBy: ['display_owner'],
            },
            {
                id: 'impacts_by_region',
                metric: 'impacts',
                source: 'summary',
                kind: 'bar',
                groupBy: ['display_tv_region'],
            },
            {
                id: 'impacts_by_day_of_week',
                metric: 'impacts',
                source: 'summary',
                kind: 'bar',
                groupBy: ['day_of_week'],
            },
            {
                id: 'impacts_by_hour',
                metric: 'impacts',
                source: 'groupByField',
                kind: 'bar',
                groupBy: ['hour'],
            },
            {
                id: 'impacts_heatmap',
                metric: 'impacts',
                source: 'summary',
                kind: 'map',
                groupBy: [],
                mapDataType: MapDataType.HeatActual,
                mapDisplayMode: 'heatmap',
            },
        ],
    },
    test: {
        standard: [
            {
                id: 'running_total_impacts',
                metric: 'impacts',
                source: 'summary',
                kind: 'line',
                groupBy: ['date'],
            },
            {
                id: 'impacts_by_media_owner',
                metric: 'impacts',
                source: 'summary',
                kind: 'bar',
                groupBy: ['display_owner'],
            },
            {
                id: 'impacts_by_environment',
                metric: 'impacts',
                source: 'groupByField',
                kind: 'bar',
                groupBy: ['display_env'],
            },
            {
                id: 'impacts_by_media_owner_format',
                metric: 'impacts',
                source: 'groupByField',
                kind: 'bar',
                groupBy: ['display_owner', 'display_format'],
            },
            {
                id: 'impacts_by_env_format',
                metric: 'impacts',
                source: 'groupByField',
                kind: 'bar',
                groupBy: ['display_env', 'display_format'],
            },
            {
                id: 'impacts_heatmap',
                metric: 'impacts',
                source: 'summary',
                kind: 'map',
                groupBy: [],
                mapDataType: MapDataType.HeatActual,
                mapDisplayMode: 'heatmap',
            },
        ],
        dsp: [
            {
                id: 'running_total_impacts',
                metric: 'impacts',
                source: 'summary',
                kind: 'line',
                groupBy: ['date'],
            },
            {
                id: 'impacts_by_media_owner',
                metric: 'impacts',
                source: 'summary',
                kind: 'bar',
                groupBy: ['display_owner'],
            },
            {
                id: 'impacts_by_environment',
                metric: 'impacts',
                source: 'groupByField',
                kind: 'bar',
                groupBy: ['display_env'],
            },
            {
                id: 'impacts_by_media_owner_format',
                metric: 'impacts',
                source: 'groupByField',
                kind: 'bar',
                groupBy: ['display_owner', 'display_format'],
            },
            {
                id: 'impacts_heatmap',
                metric: 'impacts',
                source: 'summary',
                kind: 'map',
                groupBy: [],
                mapDataType: MapDataType.HeatActual,
                mapDisplayMode: 'heatmap',
            },
        ],
    },
};

const SUMMARY_BAR_FIELDS_AVAILABLE_IN_SUMMARY = new Set([
    'date',
    'display_owner',
    'display_tv_region',
    'day_of_week',
]);

export const inferSummaryWidgetSource = (
    widget: Pick<SummaryWidgetConfig, 'kind' | 'metric' | 'groupBy'>,
): SummaryWidgetSource => {
    const [primaryGroupBy] = widget.groupBy;

    if (widget.kind === 'map') {
        return 'summary';
    }

    if (widget.kind === 'line') {
        return primaryGroupBy === 'date' ? 'summary' : 'groupByField';
    }

    if (
        widget.kind === 'bar' &&
        widget.groupBy.length === 1 &&
        SUMMARY_BAR_FIELDS_AVAILABLE_IN_SUMMARY.has(primaryGroupBy)
    ) {
        return 'summary';
    }

    return 'groupByField';
};

export const normalizeSummaryWidgetConfig = (widget: SummaryWidgetConfig): SummaryWidgetConfig => ({
    ...widget,
    groupBy: widget.kind === 'map' ? [] : widget.groupBy.filter(Boolean),
    mapDataType:
        widget.kind === 'map'
            ? widget.mapDataType === MapDataType.Marker ||
              widget.mapDataType === MapDataType.HeatInSchedule ||
              widget.mapDataType === MapDataType.HeatOutSchedule ||
              widget.mapDataType === MapDataType.HeatUnbooked
                ? MapDataType.HeatActual
                : widget.mapDataType ?? MapDataType.HeatActual
            : undefined,
    mapDisplayMode:
        widget.kind === 'map'
            ? widget.mapDataType === MapDataType.Marker
                ? 'screens'
                : widget.mapDisplayMode ?? 'heatmap'
            : undefined,
    source: inferSummaryWidgetSource(widget),
});

export const getSummaryWidgetConfigKey = (campaignId: number, isDsp: boolean) =>
    `${campaignId}:${isDsp ? 'dsp' : 'standard'}`;

export const getDefaultSummaryWidgets = (isDsp: boolean, preset: SummaryPresetKey = 'default') =>
    (isDsp ? SUMMARY_WIDGET_PRESETS[preset].dsp : SUMMARY_WIDGET_PRESETS[preset].standard).map(
        widget =>
            normalizeSummaryWidgetConfig({
                ...widget,
                groupBy: [...widget.groupBy],
            }),
    );

export const createEmptySummaryWidget = (): SummaryWidgetConfig => ({
    id: `summary_widget_${Date.now()}`,
    metric: 'impacts',
    kind: 'bar',
    groupBy: ['display_owner'],
    source: 'summary',
    mapDataType: undefined,
    mapDisplayMode: undefined,
});
